// ============================================================
// PanelAmbitoVacaciones.jsx — Ámbito por empresa (Fase 4.2)
// Se renderiza DENTRO de GestorPermisos (nodo especial "Ámbito por empresa"
// bajo Vacaciones). Consume PeticionesVacaciones (endpoints 4.1). DS v2.
//
// Regla: DS v2 SOLO aquí (dentro de /configuracion/permisos). /panel/vacaciones
// conserva su MUI legacy y no se toca.
//
// La búsqueda de personas usa la lista `usuarios` (internos) que GestorPermisos ya
// carga y pasa por props — mismo patrón que MatrizAccesoTipos. Internos por
// construcción: los colaboradores no aparecen (esta fase no los configura; el
// servidor devuelve 403). No se usa PersonaPicker (mezcla colaboradores y no se
// puede filtrar sin tocarlo).
// ============================================================

import React, { useState, useEffect, useMemo, useRef, useContext, useCallback } from 'react';
import {
  Umbrella, Plus, Pencil, Lock, Building, Users, Loader2, Save, Trash2,
  Eye, ShieldCheck, AlertTriangle, UserCog, Search, List, LayoutGrid,
} from 'lucide-react';
import {
  Button, Select, Card, CardContent, Badge, Avatar, Checkbox,
  Dialog, Table, THead, TBody, Tr, Th, Td, cn,
} from './ui';
import {
  fetchConfiguracionAmbito, calcularAlcanceAmbito,
  guardarAmbitoBeneficiario, revocarAmbitoBeneficiario,
} from '../../peticiones_API/PeticionesVacaciones';
import { fetchRolesData } from '../../peticiones_API/PeticionesGestionPersonasPersonas';
import { UsuarioContext } from '../providers/personaContext';

const SUPER_ADMIN = 'Super Administrador';
const vacio = () => ({ todosNegocios: false, todasColaboradoras: false, negocios: [], colaboradoras: [] });
const nombrePersona = (u) => `${u.nombre || ''} ${u.apellidos || ''}`.trim();
const idsDe = (arr) => (arr || []).map((x) => x.id ?? x);

// beneficiario.ver/gestionar (forma del GET) → modelo del diálogo.
function modeloDesde(ver = vacio(), gestionar = vacio()) {
  const gestNoVacio = !!(gestionar.todosNegocios || gestionar.todasColaboradoras
    || (gestionar.negocios || []).length || (gestionar.colaboradoras || []).length);
  return {
    nivel: gestNoVacio ? 'gestionar' : 'ver',
    empTodas: !!ver.todosNegocios,
    empIds: new Set(idsDe(ver.negocios)),
    colTodas: !!ver.todasColaboradoras,
    colIds: new Set(idsDe(ver.colaboradoras)),
  };
}
function cuerpoDesde(m) {
  const ver = {
    todosNegocios: m.empTodas, todasColaboradoras: m.colTodas,
    negocios: m.empTodas ? [] : [...m.empIds], colaboradoras: m.colTodas ? [] : [...m.colIds],
  };
  const gestionar = m.nivel === 'gestionar'
    ? { todosNegocios: ver.todosNegocios, todasColaboradoras: ver.todasColaboradoras, negocios: [...ver.negocios], colaboradoras: [...ver.colaboradoras] }
    : vacio();
  return { ver, gestionar };
}
const modeloVacio = () => ({ nivel: 'ver', empTodas: false, empIds: new Set(), colTodas: false, colIds: new Set() });
const modeloTieneDestino = (m) => m.empTodas || m.colTodas || m.empIds.size > 0 || m.colIds.size > 0;
const tieneGestion = (b) => !!(b.gestionar && (b.gestionar.todosNegocios || b.gestionar.todasColaboradoras || (b.gestionar.negocios || []).length || (b.gestionar.colaboradoras || []).length));

// Niveles MIXTOS: hay gestión y además destinos que solo están en ver (se ascenderían al unificar).
function nivelMixto(ver = vacio(), gestionar = vacio()) {
  const gAlgo = gestionar.todosNegocios || gestionar.todasColaboradoras || (gestionar.negocios || []).length || (gestionar.colaboradoras || []).length;
  if (!gAlgo) return false;
  const gNeg = new Set(idsDe(gestionar.negocios));
  const gCol = new Set(idsDe(gestionar.colaboradoras));
  const verOnlyNeg = (ver.todosNegocios && !gestionar.todosNegocios) || idsDe(ver.negocios).some((id) => !gNeg.has(id));
  const verOnlyCol = (ver.todasColaboradoras && !gestionar.todasColaboradoras) || idsDe(ver.colaboradoras).some((id) => !gCol.has(id));
  return !!(verOnlyNeg || verOnlyCol);
}

// ------------------------------------------------------------
// Chips del alcance de una fila (solo lectura).
// ------------------------------------------------------------
function ChipsAlcance({ ver = vacio() }) {
  const chips = [];
  if (ver.todosNegocios) chips.push({ k: 'ne-all', txt: 'Todas las empresas', v: 'info' });
  else (ver.negocios || []).forEach((n) => chips.push({ k: 'ne-' + n.id, txt: n.nombre || `Empresa ${n.id}`, v: 'secondary' }));
  if (ver.todasColaboradoras) chips.push({ k: 'co-all', txt: 'Todas las colaboradoras', v: 'info' });
  else (ver.colaboradoras || []).forEach((c) => chips.push({ k: 'co-' + c.id, txt: c.nombre || `Colab. ${c.id}`, v: 'secondary' }));
  if (!chips.length) return <span className="text-xs text-slate-400 italic">Sin ámbito</span>;
  const vis = chips.slice(0, 4);
  const resto = chips.length - vis.length;
  return (
    <div className="flex flex-wrap gap-1">
      {vis.map((c) => <Badge key={c.k} variant={c.v}>{c.txt}</Badge>)}
      {resto > 0 && <Badge variant="secondary">+{resto}</Badge>}
    </div>
  );
}

// ------------------------------------------------------------
// Buscador de personas INTERNAS (sobre la lista `usuarios`). Sin colaboradores.
// ------------------------------------------------------------
function BuscadorInternos({ usuarios, onElegir }) {
  const [q, setQ] = useState('');
  const query = q.trim().toLowerCase();
  const res = query
    ? (usuarios || []).filter((u) => nombrePersona(u).toLowerCase().includes(query) || (u.mail_empresa || '').toLowerCase().includes(query)).slice(0, 20)
    : [];
  return (
    <div>
      <div className="relative">
        <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={q} onChange={(e) => setQ(e.target.value)} autoFocus
          placeholder="Buscar persona interna…"
          className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900/10"
        />
      </div>
      {query && (
        <div className="mt-1.5 max-h-52 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {res.length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-400 italic">Sin personas internas que coincidan.</div>
          ) : res.map((u) => (
            <button key={u.Id} type="button" onClick={() => onElegir(u)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-slate-50">
              <Avatar name={nombrePersona(u)} size="sm" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-slate-900 truncate">{nombrePersona(u)}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.rol || 'Sin rol'}{u.mail_empresa ? ` · ${u.mail_empresa}` : ''}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      <p className="text-[11px] text-slate-400 mt-1">Solo personas internas. Las colaboradoras se muestran en la tabla en solo lectura (no se configuran en esta fase).</p>
    </div>
  );
}

// ------------------------------------------------------------
// Selector de beneficiario (solo al CONCEDER sin objetivo previo).
// ------------------------------------------------------------
function SelectorBeneficiario({ roles, usuarios, onElegir }) {
  const [modo, setModo] = useState('rol'); // 'rol' | 'persona'
  const [rol, setRol] = useState('');
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Button variant={modo === 'rol' ? 'primary' : 'outline'} size="sm" onClick={() => setModo('rol')}><ShieldCheck className="size-3.5" /> Rol</Button>
        <Button variant={modo === 'persona' ? 'primary' : 'outline'} size="sm" onClick={() => setModo('persona')}><UserCog className="size-3.5" /> Persona interna</Button>
      </div>
      {modo === 'rol' ? (
        <Select value={rol} onChange={(e) => { const v = e.target.value; setRol(v); if (v) onElegir({ tipoBeneficiario: 'rol', id: v, nombre: v, subtitulo: 'Rol del sistema' }); }} prefix={<ShieldCheck size={14} />}>
          <option value="">Elige un rol…</option>
          {roles.filter((r) => r !== SUPER_ADMIN).map((r) => <option key={r} value={r}>{r}</option>)}
        </Select>
      ) : (
        <BuscadorInternos usuarios={usuarios} onElegir={(u) => onElegir({ tipoBeneficiario: 'persona', id: u.Id, nombre: nombrePersona(u), subtitulo: u.rol || 'Persona interna' })} />
      )}
    </div>
  );
}

// Un eje (empresas o colaboradoras): comodín-o-concretas (mutuamente excluyentes).
function EjeSelector({ titulo, todas, setTodas, etiquetaTodas, items, sel, toggle, vacio: textoVacio }) {
  return (
    <div className="rounded-lg border border-slate-200 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">
        <Building className="size-4 text-[hsl(var(--brand-light))]" /> {titulo}
      </div>
      <div className="space-y-1.5">
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input type="radio" checked={todas} onChange={() => setTodas(true)} className="accent-slate-900 mt-0.5" />
          <span className="text-sm text-slate-800">{etiquetaTodas}</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer select-none">
          <input type="radio" checked={!todas} onChange={() => setTodas(false)} className="accent-slate-900" />
          <span className="text-sm text-slate-800">Concretas</span>
        </label>
      </div>
      {!todas && (
        <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 divide-y divide-slate-100">
          {(items || []).length === 0 ? (
            <div className="px-3 py-3 text-xs text-slate-400 italic">{textoVacio || 'Sin opciones.'}</div>
          ) : items.map((it) => (
            <label key={it.id} className="flex items-center gap-2.5 px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
              <Checkbox checked={sel.has(it.id)} onChange={() => toggle(it.id)} />
              <span className="flex-1 text-slate-800 truncate">{it.nombre}</span>
              <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{it.personas} pers.</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------
// Diálogo de ámbito.
// ------------------------------------------------------------
function DialogoAmbito({ open, onClose, beneficiario, targetInicial, catalogos, roles, usuarios, existentes, onGuardado }) {
  const editando = !!beneficiario;
  const [target, setTarget] = useState(null);
  const [avisoReemplazo, setAvisoReemplazo] = useState(false);
  const [avisoMixto, setAvisoMixto] = useState(false);
  const [modelo, setModelo] = useState(modeloVacio());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [confirmandoRevoca, setConfirmandoRevoca] = useState(false);
  const [revocando, setRevocando] = useState(false);
  const [alcance, setAlcance] = useState({ estado: 'idle', valor: null });
  const debounceRef = useRef(null);
  const reqSeq = useRef(0);

  useEffect(() => {
    if (!open) return;
    setError(null); setConfirmandoRevoca(false);
    if (beneficiario) {
      setTarget({ tipoBeneficiario: beneficiario.tipo, id: beneficiario.id, nombre: beneficiario.nombre, subtitulo: beneficiario.subtitulo });
      setModelo(modeloDesde(beneficiario.ver, beneficiario.gestionar));
      setAvisoReemplazo(false);
      setAvisoMixto(nivelMixto(beneficiario.ver, beneficiario.gestionar));
    } else if (targetInicial) {
      setTarget(targetInicial);
      setModelo(modeloVacio());
      setAvisoReemplazo(false); setAvisoMixto(false);
    } else {
      setTarget(null); setModelo(modeloVacio()); setAvisoReemplazo(false); setAvisoMixto(false);
    }
  }, [open, beneficiario, targetInicial]);

  useEffect(() => {
    if (!open || !target) { setAlcance({ estado: 'idle', valor: null }); return; }
    if (!modeloTieneDestino(modelo)) { setAlcance({ estado: 'ok', valor: 0 }); return; }
    setAlcance({ estado: 'loading', valor: null });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const cuerpo = cuerpoDesde(modelo);
    const seq = ++reqSeq.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await calcularAlcanceAmbito([{ ref: 'd', ver: cuerpo.ver, gestionar: cuerpo.gestionar }]);
        if (seq !== reqSeq.current) return;
        setAlcance({ estado: 'ok', valor: (data.resultados || [])[0]?.alcance ?? 0 });
      } catch (e) {
        if (seq !== reqSeq.current) return;
        setAlcance({ estado: 'error', valor: null });
      }
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [open, target, modelo]);

  const setNivel = (n) => setModelo((m) => ({ ...m, nivel: n }));
  const toggleEmp = (id) => setModelo((m) => { const s = new Set(m.empIds); s.has(id) ? s.delete(id) : s.add(id); return { ...m, empIds: s }; });
  const toggleCol = (id) => setModelo((m) => { const s = new Set(m.colIds); s.has(id) ? s.delete(id) : s.add(id); return { ...m, colIds: s }; });

  const guardar = async () => {
    if (!target || !modeloTieneDestino(modelo)) return;
    setGuardando(true); setError(null);
    try {
      const res = await guardarAmbitoBeneficiario(target.tipoBeneficiario, target.id, cuerpoDesde(modelo));
      await onGuardado(res);
    } catch (e) { setError(e.response?.data?.message || 'Error al guardar el ámbito.'); setGuardando(false); }
  };
  const revocar = async () => {
    if (!target) return;
    setRevocando(true); setError(null);
    try { await revocarAmbitoBeneficiario(target.tipoBeneficiario, target.id); await onGuardado(null); }
    catch (e) { setError(e.response?.data?.message || 'Error al revocar.'); setRevocando(false); setConfirmandoRevoca(false); }
  };

  const contador = (
    <span className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900 tabular-nums">
      {alcance.estado === 'loading' && <><Loader2 className="size-3.5 animate-spin text-[hsl(var(--brand-light))]" /> calculando…</>}
      {alcance.estado === 'error' && <span className="text-amber-600 inline-flex items-center gap-1"><AlertTriangle className="size-3.5" /> — (no se pudo calcular)</span>}
      {alcance.estado === 'ok' && <><Users className="size-3.5 text-[hsl(var(--brand-light))]" /> {alcance.valor} personas</>}
      {alcance.estado === 'idle' && <span className="text-slate-400">—</span>}
    </span>
  );

  const footer = (
    <div className="flex items-center justify-between w-full gap-3">
      <div className="flex items-center gap-3">
        {target && (
          <span className="inline-flex items-center gap-2">
            <span className="text-[11px] text-slate-500">Alcance:</span>{contador}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2">
        {editando && target && <Button variant="danger" onClick={() => setConfirmandoRevoca(true)} disabled={guardando || revocando}><Trash2 className="size-3.5" /> Revocar</Button>}
        <Button variant="ghost" onClick={onClose} disabled={guardando || revocando}>Cancelar</Button>
        <Button onClick={guardar} disabled={!target || !modeloTieneDestino(modelo) || guardando}>
          {guardando ? <Loader2 className="size-3.5 animate-spin" /> : <Save className="size-3.5" />} {guardando ? 'Guardando…' : 'Guardar ámbito'}
        </Button>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onClose={onClose} size="xl"
      title={editando ? `Ámbito · ${beneficiario?.nombre}` : 'Conceder ámbito'}
      description={editando ? beneficiario?.subtitulo : 'Elige un beneficiario y define qué vacaciones podrá ver o gestionar.'}
      footer={footer}>
      <div className="space-y-4">
        {/* Beneficiario: selector si es nuevo sin objetivo; colapsado si ya elegido */}
        {!target ? (
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Beneficiario</div>
            <SelectorBeneficiario roles={roles} usuarios={usuarios}
              onElegir={(t) => {
                if (!t.id) return;
                setTarget(t);
                const ya = existentes.find((b) => b.tipo === t.tipoBeneficiario && String(b.id) === String(t.id));
                setAvisoReemplazo(!!ya);
                setModelo(ya ? modeloDesde(ya.ver, ya.gestionar) : modeloVacio());
                setAvisoMixto(ya ? nivelMixto(ya.ver, ya.gestionar) : false);
              }} />
          </div>
        ) : (
          <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            {target.tipoBeneficiario === 'rol' ? <ShieldCheck className="size-5 text-slate-400" /> : <Avatar name={target.nombre} size="sm" />}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{target.nombre}</div>
              <div className="text-[11px] text-slate-500 truncate">{target.tipoBeneficiario === 'rol' ? 'Rol del sistema' : target.subtitulo}</div>
            </div>
            {!editando && (
              <button type="button" onClick={() => { setTarget(null); setAvisoReemplazo(false); setAvisoMixto(false); }}
                className="text-xs text-slate-500 hover:text-slate-900 hover:underline">cambiar</button>
            )}
          </div>
        )}
        {avisoReemplazo && <p className="text-[11px] text-amber-600 flex items-center gap-1"><AlertTriangle className="size-3.5 shrink-0" /> Ya tiene ámbito configurado; al guardar se reemplazará.</p>}

        {target && (
          <>
            {/* Nivel */}
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Nivel</div>
              <div className="grid grid-cols-2 gap-2">
                {[['ver', 'Solo ver', 'Ve el calendario; no aprueba.', Eye], ['gestionar', 'Ver y gestionar', 'Ve y aprueba/rechaza.', ShieldCheck]].map(([val, tit, sub, Icono]) => (
                  <button key={val} type="button" onClick={() => setNivel(val)}
                    className={cn('rounded-lg border px-3 py-2.5 text-left transition-colors', modelo.nivel === val ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 hover:bg-slate-50')}>
                    <div className="flex items-center gap-1.5 text-sm font-medium"><Icono className="size-4" /> {tit}</div>
                    <div className={cn('text-[11px] mt-0.5', modelo.nivel === val ? 'text-white/70' : 'text-slate-500')}>{sub}</div>
                  </button>
                ))}
              </div>
              {avisoMixto && (
                <p className="text-[11px] text-amber-600 mt-1.5 flex items-start gap-1">
                  <AlertTriangle className="size-3.5 shrink-0 mt-px" />
                  Este beneficiario tiene niveles distintos por empresa. Al guardar se unificarán a «{modelo.nivel === 'gestionar' ? 'Ver y gestionar' : 'Solo ver'}».
                </p>
              )}
            </div>

            {/* Ejes en dos columnas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <EjeSelector titulo="Empresas del grupo" todas={modelo.empTodas} setTodas={(v) => setModelo((m) => ({ ...m, empTodas: v }))}
                etiquetaTodas="Todas las empresas del grupo (incluye las que se creen)" items={catalogos.negocios} sel={modelo.empIds} toggle={toggleEmp} />
              <EjeSelector titulo="Empresas colaboradoras" todas={modelo.colTodas} setTodas={(v) => setModelo((m) => ({ ...m, colTodas: v }))}
                etiquetaTodas="Todas las colaboradoras (incluye las que se creen)" items={catalogos.colaboradoras} sel={modelo.colIds} toggle={toggleCol}
                vacio="No hay colaboradoras con el módulo de vacaciones activo." />
            </div>

            {error && <div className="px-4 py-3 rounded-lg text-sm font-medium bg-red-50 text-red-700 flex items-center gap-2"><AlertTriangle className="size-4 shrink-0" /> {error}</div>}
          </>
        )}
      </div>

      <Dialog open={confirmandoRevoca} onClose={() => !revocando && setConfirmandoRevoca(false)} size="sm" title="Revocar ámbito"
        footer={
          <div className="flex justify-end gap-2 w-full">
            <Button variant="ghost" onClick={() => setConfirmandoRevoca(false)} disabled={revocando}>Cancelar</Button>
            <Button variant="danger" onClick={revocar} disabled={revocando}>{revocando ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />} {revocando ? 'Revocando…' : 'Revocar'}</Button>
          </div>
        }>
        <p className="text-sm text-slate-600">
          Se quitará todo el ámbito de <span className="font-semibold text-slate-900">{beneficiario?.nombre}</span>.
          {alcance.estado === 'ok' && alcance.valor > 0 && <> Dejará de ver a <span className="font-semibold text-slate-900">{alcance.valor} personas</span>.</>}
        </p>
      </Dialog>
    </Dialog>
  );
}

// ------------------------------------------------------------
// Vista "Por empresa": cada empresa con quién la alcanza (concreto vs comodín).
// ------------------------------------------------------------
function VistaPorEmpresa({ beneficiarios, catalogos }) {
  const filas = (items, eje) => items.map((it) => {
    const alcanzan = [];
    for (const b of beneficiarios) {
      const ejeV = b.ver || vacio();
      const porComodin = eje === 'neg' ? ejeV.todosNegocios : ejeV.todasColaboradoras;
      const concreto = (eje === 'neg' ? idsDe(ejeV.negocios) : idsDe(ejeV.colaboradoras)).includes(it.id);
      if (porComodin || concreto) alcanzan.push({ b, via: porComodin ? 'comodín' : 'concreto' });
    }
    return { it, alcanzan };
  });
  const bloque = (titulo, items, eje) => (
    <div className="mb-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">{titulo}</div>
      <div className="border border-slate-200 rounded-lg divide-y divide-slate-100">
        {items.length === 0 ? <div className="px-3 py-3 text-xs text-slate-400 italic">Sin empresas.</div> :
          filas(items, eje).map(({ it, alcanzan }) => (
            <div key={it.id} className="px-3 py-2.5">
              <div className="flex items-center justify-between gap-2 mb-1">
                <span className="text-sm font-medium text-slate-900 truncate">{it.nombre}</span>
                <span className="text-[11px] text-slate-400 tabular-nums shrink-0">{it.personas} pers.</span>
              </div>
              {alcanzan.length === 0 ? <span className="text-[11px] text-slate-400 italic">Nadie con ámbito específico (además del acceso global).</span> : (
                <div className="flex flex-wrap gap-1">
                  {alcanzan.map(({ b, via }) => (
                    <span key={`${b.tipo}:${b.id}`} className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700">
                      {b.tipo === 'rol' ? <ShieldCheck className="size-3 text-slate-400" /> : <Users className="size-3 text-slate-400" />}
                      {b.nombre}
                      {via === 'comodín' && <Badge variant="info">comodín</Badge>}
                    </span>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
  return <div>{bloque('Empresas del grupo', catalogos.negocios || [], 'neg')}{bloque('Empresas colaboradoras', catalogos.colaboradoras || [], 'col')}</div>;
}

// ------------------------------------------------------------
// Panel principal.
// ------------------------------------------------------------
export default function PanelAmbitoVacaciones({ usuarios = [] }) {
  const { recargarAmbito } = useContext(UsuarioContext);
  const [cfg, setCfg] = useState(null);
  const [roles, setRoles] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState('beneficiarios'); // 'beneficiarios' | 'empresas'
  const [busqueda, setBusqueda] = useState('');
  const [dialogo, setDialogo] = useState({ open: false, beneficiario: null, targetInicial: null });
  // Cambio B: eliminar desde la tabla (confirmación con alcance; sin optimista).
  const [confirmRevoca, setConfirmRevoca] = useState(null); // el beneficiario a eliminar, o null
  const [revocandoBusy, setRevocandoBusy] = useState(false);
  const [revocaError, setRevocaError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true); setError(null);
    try {
      const [c, r] = await Promise.all([fetchConfiguracionAmbito(), fetchRolesData()]);
      setCfg(c);
      setRoles(Array.isArray(r) ? r.map((x) => x.nombre || x).filter(Boolean) : []);
    } catch (e) { setError('No se pudo cargar la configuración de ámbito.'); }
    finally { setCargando(false); }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const beneficiarios = useMemo(() => cfg?.beneficiarios || [], [cfg]);
  const catalogos = useMemo(() => cfg?.catalogos || { negocios: [], colaboradoras: [] }, [cfg]);

  const kpis = useMemo(() => ({
    total: beneficiarios.length,
    conGestion: beneficiarios.filter(tieneGestion).length,
    soloLectura: beneficiarios.filter((x) => x.soloLectura).length,
  }), [beneficiarios]);

  const q = busqueda.trim().toLowerCase();
  const benefFiltrados = q ? beneficiarios.filter((b) => (b.nombre || '').toLowerCase().includes(q)) : beneficiarios;
  const idsPersonaConAmbito = useMemo(() => new Set(beneficiarios.filter((b) => b.tipo === 'persona').map((b) => String(b.id))), [beneficiarios]);
  const sinAmbito = q
    ? (usuarios || []).filter((u) => !idsPersonaConAmbito.has(String(u.Id)) && (nombrePersona(u).toLowerCase().includes(q) || (u.mail_empresa || '').toLowerCase().includes(q))).slice(0, 10)
    : [];

  const trasCambio = async () => {
    setDialogo({ open: false, beneficiario: null, targetInicial: null });
    await cargar();
    try { if (recargarAmbito) await recargarAmbito(); } catch (_) { /* no crítico */ }
  };

  // Cambio B: elimina el ámbito del beneficiario. SIN optimista: revoca, refresca del servidor,
  // y recargarAmbito() por si el borrado afecta a un rol que TÚ tienes (igual que al guardar).
  const ejecutarRevoca = async () => {
    if (!confirmRevoca) return;
    setRevocandoBusy(true); setRevocaError(null);
    try {
      await revocarAmbitoBeneficiario(confirmRevoca.tipo, confirmRevoca.id);
      setConfirmRevoca(null);
      await cargar();
      try { if (recargarAmbito) await recargarAmbito(); } catch (_) { /* no crítico */ }
    } catch (e) {
      setRevocaError(e.response?.data?.message || 'No se pudo eliminar el ámbito.');
    } finally { setRevocandoBusy(false); }
  };

  if (cargando) return <div className="py-16 text-center text-slate-500 text-sm"><Loader2 className="size-5 text-[hsl(var(--brand-light))] mx-auto animate-spin mb-2" /> Cargando ámbito de vacaciones…</div>;
  if (error) return (
    <div className="py-10 text-center">
      <AlertTriangle className="size-6 text-amber-500 mx-auto mb-2" />
      <div className="text-sm text-slate-600 mb-3">{error}</div>
      <Button variant="outline" size="sm" onClick={cargar}>Reintentar</Button>
    </div>
  );

  return (
    <Card>
      <CardContent className="p-4">
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Umbrella className="size-5 text-[hsl(var(--brand-light))]" strokeWidth={1.75} />
            <div>
              <div className="text-sm font-semibold text-slate-900">Ámbito por empresa</div>
              <div className="text-[11px] text-slate-500">Quién ve o gestiona las vacaciones de qué empresas y colaboradoras.</div>
            </div>
          </div>
          <Button onClick={() => setDialogo({ open: true, beneficiario: null, targetInicial: null })}><Plus className="size-3.5" /> Conceder ámbito</Button>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          {[['Beneficiarios', kpis.total], ['Con gestión', kpis.conGestion], ['Solo lectura', kpis.soloLectura]].map(([t, n]) => (
            <div key={t} className="rounded-lg border border-slate-200 px-3 py-2.5">
              <div className="text-[11px] text-slate-500">{t}</div>
              <div className="text-lg font-semibold text-slate-900 tabular-nums">{n}</div>
            </div>
          ))}
        </div>

        {/* Toggle de vista */}
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="inline-flex gap-1 p-1 bg-slate-100 rounded-md">
            <button type="button" onClick={() => setVista('beneficiarios')} className={cn('inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors', vista === 'beneficiarios' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600')}><List size={14} /> Por beneficiario</button>
            <button type="button" onClick={() => setVista('empresas')} className={cn('inline-flex items-center gap-1.5 px-3 py-1 text-sm rounded transition-colors', vista === 'empresas' ? 'bg-white shadow-sm text-slate-900 font-medium' : 'text-slate-600')}><LayoutGrid size={14} /> Por empresa</button>
          </div>
          {vista === 'beneficiarios' && (
            <div className="relative w-64 max-w-full">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={busqueda} onChange={(e) => setBusqueda(e.target.value)} placeholder="Buscar beneficiario o persona…"
                className="w-full pl-8 pr-3 py-1.5 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-900/10" />
            </div>
          )}
        </div>

        {vista === 'empresas' ? (
          <VistaPorEmpresa beneficiarios={beneficiarios} catalogos={catalogos} />
        ) : (
          <>
            {beneficiarios.length === 0 ? (
              <div className="py-12 text-center text-slate-400"><Umbrella className="size-7 mx-auto mb-3" strokeWidth={1.5} /><div className="text-sm">Aún no hay ámbitos concedidos. Usa «Conceder ámbito».</div></div>
            ) : (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <Table>
                  <THead><Tr><Th>Beneficiario</Th><Th>Alcance</Th><Th>Nivel</Th><Th align="right">Personas</Th><Th align="right"></Th></Tr></THead>
                  <TBody>
                    {benefFiltrados.map((b) => (
                      <Tr key={`${b.tipo}:${b.id}`}>
                        <Td>
                          <div className="flex items-center gap-2.5">
                            {b.tipo === 'rol' ? <ShieldCheck className="size-5 text-slate-400 shrink-0" /> : <Avatar name={b.nombre} size="sm" />}
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">{b.nombre}{b.soloLectura && <Lock className="size-3 text-slate-400" />}</div>
                              <div className="text-[11px] text-slate-500 truncate">{b.subtitulo}</div>
                            </div>
                          </div>
                        </Td>
                        <Td><ChipsAlcance ver={b.ver} /></Td>
                        <Td>{tieneGestion(b) ? <Badge variant="primary">Gestionar</Badge> : <Badge variant="secondary">Solo ver</Badge>}</Td>
                        <Td align="right"><span className="text-sm font-medium text-slate-900 tabular-nums">{b.alcance}</span></Td>
                        <Td align="right">{b.soloLectura ? <span className="text-[11px] text-slate-400 italic">solo lectura</span> : (
                          <div className="inline-flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => setDialogo({ open: true, beneficiario: b, targetInicial: null })}><Pencil className="size-3.5" /> Editar</Button>
                            <Button variant="ghost" size="icon" title="Eliminar ámbito" onClick={() => { setRevocaError(null); setConfirmRevoca(b); }}><Trash2 className="size-3.5 text-rose-600" /></Button>
                          </div>
                        )}</Td>
                      </Tr>
                    ))}
                  </TBody>
                </Table>
              </div>
            )}
            {q && benefFiltrados.length === 0 && sinAmbito.length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">Nadie coincide con «{busqueda}».</div>
            )}

            {/* Personas SIN ámbito que coinciden con la búsqueda */}
            {sinAmbito.length > 0 && (
              <div className="mt-3">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5">Sin ámbito concedido</div>
                <div className="border border-dashed border-slate-200 rounded-lg divide-y divide-slate-100">
                  {sinAmbito.map((u) => (
                    <div key={u.Id} className="flex items-center gap-2.5 px-3 py-2">
                      <Avatar name={nombrePersona(u)} size="sm" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-900 truncate">{nombrePersona(u)}</div>
                        <div className="text-[11px] text-slate-400 truncate">{u.rol || 'Sin rol'} · sin ámbito concedido</div>
                      </div>
                      <Button variant="outline" size="sm" onClick={() => setDialogo({ open: true, beneficiario: null, targetInicial: { tipoBeneficiario: 'persona', id: u.Id, nombre: nombrePersona(u), subtitulo: u.rol || 'Persona interna' } })}>
                        <Plus className="size-3.5" /> Conceder
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* Nota Super Admin */}
        <div className="mt-4 flex items-center gap-2 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
          <ShieldCheck className="size-3.5 shrink-0 text-slate-400" />
          El Super Administrador ve y gestiona todo el grupo por su acceso global; no aparece aquí porque no se configura.
        </div>
      </CardContent>

      <DialogoAmbito open={dialogo.open} beneficiario={dialogo.beneficiario} targetInicial={dialogo.targetInicial}
        catalogos={catalogos} roles={roles} usuarios={usuarios} existentes={beneficiarios}
        onClose={() => setDialogo({ open: false, beneficiario: null, targetInicial: null })} onGuardado={trasCambio} />

      {/* Cambio B: confirmación de eliminar desde la tabla. Reversible → sin palabra escrita. */}
      <Dialog open={!!confirmRevoca} onClose={() => !revocandoBusy && setConfirmRevoca(null)} size="sm" title="Eliminar ámbito"
        footer={<>
          <Button variant="outline" onClick={() => setConfirmRevoca(null)} disabled={revocandoBusy}>Cancelar</Button>
          <Button variant="danger" onClick={ejecutarRevoca} disabled={revocandoBusy}>
            {revocandoBusy ? <><Loader2 className="size-3.5 animate-spin" /> Eliminando…</> : <><Trash2 className="size-3.5" /> Eliminar</>}
          </Button>
        </>}>
        {confirmRevoca && (
          <div className="space-y-2 text-sm text-slate-600">
            <p>Vas a eliminar el ámbito de <span className="font-semibold text-slate-900">{confirmRevoca.nombre}</span>.</p>
            {confirmRevoca.alcance > 0 && <p>Dejará de ver a <span className="font-semibold text-slate-900">{confirmRevoca.alcance} personas</span>.</p>}
            {confirmRevoca.tipo === 'rol' && (
              <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-amber-800">
                <AlertTriangle className="size-4 mt-0.5 shrink-0" />
                <span>Afecta a <strong>todas las personas con el rol «{confirmRevoca.nombre}»</strong>, no a una sola.</span>
              </div>
            )}
            {revocaError && <p className="text-rose-600">{revocaError}</p>}
          </div>
        )}
      </Dialog>
    </Card>
  );
}
